import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import moment from 'moment';
import { companyIdAtom } from 'views/state';

import { CAType } from './config';
import {
  applyIntervalFilter,
  applyTimespanFilter,
  checkNonZeroArray,
  checkNonZeroObj,
  getAllIndexes,
  getNewDataGenerationDate,
  mergeArrays,
} from '../helper';
import {
  DashboardType,
  GroupingDataItem,
  IntervalFilter,
  IntervalFilterType,
  Severity,
  TimespanFilter,
  VulnerabilityStatus,
  VulnStatus,
} from '../types';

const EMPTY_GROUPING_DATA: GroupingDataItem[] = [];

// DASHBOARD FILTERS
export const severityFiltersAtom = atomWithStorage<string[]>(
  'ca_dashboard_severity_filter',
  [
    Severity[Severity.Urgent],
    Severity[Severity.Critical],
    Severity[Severity.Serious],
    Severity[Severity.Medium],
    Severity[Severity.Minimal],
  ],
);
export const showHistoryChartCountsAtom = atomWithStorage(
  'ca_dashboard_show_counts',
  false,
);

export const timeSpanAtom = atomWithReset(TimespanFilter.SixMonth);
export const intervalAtom = atomWithReset(IntervalFilter.Week);
export const caRefreshAtom = atomWithStorage<string>('ca_refresh', '');
export const caCurrentVulnCountAtom = atomWithStorage<string>(
  'caCurrentVulnCount',
  '',
);

export const historyChartsOpenAtom = atom(true);
export const currentVulnChartsOpenAtom = atom(true);

export const caTypeAtom = atomWithReset(CAType.All);
// selected CA's for individual dashboards
export const appliedCAIdsAtom = atomWithReset<number[]>([]);

export const isObsoleteGroupingVulnListingOpenAtom = atom(false);
export const isGroupingByQidVulnListingOpenAtom = atom(false);

export const dashboardDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const appliedCAIds = get(appliedCAIdsAtom);
  const _refresh = get(caRefreshAtom);

  try {
    if (companyId && companyId !== -1) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = {
        company_id: companyId,
        host_type: DashboardType.CA,
        timezone: userTimezone,
      };
      if (appliedCAIds?.length > 0) {
        params['obj_id_list'] = appliedCAIds;
      }

      const responseData = await callApi('dashboard/', 'GET', params);
      return responseData?.data;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
});

export const hostCountsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);
  if (!data?.hosts_counts)
    return {
      [CAType.Server]: 0,
      [CAType.Workstation]: 0,
    };
  return data.hosts_counts;
});

export const historyChartLabelsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  return (
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.weeks, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    []
  );
});

export const newDataGenerationIndexAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  const weeks = get(historyChartLabelsAtom);
  const years =
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.years, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    [];

  const dateInfo = getNewDataGenerationDate(DashboardType.CA);
  const weekIndexes = getAllIndexes(weeks, dateInfo.week);
  let newDataGenerationIndex = -1;
  weekIndexes.forEach((i) => {
    if (years[i] === dateInfo.year) {
      newDataGenerationIndex = i;
    }
  });
  return newDataGenerationIndex;
});

export const historyChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);

  if (data) {
    const historyChartSeverityData = {};
    const historyChartLastIntervalSeverityData = {};
    for (const status in VulnStatus) {
      historyChartSeverityData[status] = getHistoryChartData(
        data.historical_data,
        status as VulnStatus,
        caType,
        severityFilters,
        timespanFilter,
        intervalFilter,
        'severity',
      );
      if (status !== VulnStatus.All) {
        historyChartLastIntervalSeverityData[status] =
          getHistoryChartIntervalData(
            data.historical_data,
            status as VulnStatus,
            caType,
            severityFilters,
            timespanFilter,
            intervalFilter,
            'last_interval_days',
          );
      }
    }

    return {
      historyChartSeverityData,
      historyChartLastIntervalSeverityData,
    };
  }
  return undefined;
});

export const vulnBySeverityChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  if (data) {
    return getVulnBySeverityChartData(data.vulns_by_severity, caType);
  }
  return undefined;
});

export const LastSyncDateAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data) {
    return moment(data.historical_data?.last_sync_date).format('lll');
  }
  return undefined;
});

export const vulnBySeverityChartDataCountAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnBySeverityChartDataCount(
      data.vulns_by_severity,
      caType,
      severityFilters,
    );
  }
  return undefined;
});

export const patchableVulnBySeverityChartDataCountAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnBySeverityChartDataCount(
      data.patchable_vulns_by_severity,
      caType,
      severityFilters,
    );
  }
  return undefined;
});

export const obsoleteVulnGroupsChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  if (data) {
    return geObsoleteVulnGroupsData(data.obsolete_vuln_groups, caType);
  }
  return undefined;
});

export const patchableVulnBySeverityChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  if (data) {
    return getVulnBySeverityChartData(data.patchable_vulns_by_severity, caType);
  }
  return undefined;
});

export const vulnGroupsChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnGroupsData(data.vuln_group, caType, severityFilters);
  }
  return undefined;
});

export const severityChartLabelsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data?.vulns_by_severity) {
    return Object.keys(data.vulns_by_severity[CAType.Server]);
  }
  return [];
});

export const vulnByStatusChartDataAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  const caType = get(caTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnByStatusChartData(
      data.vulns_by_status,
      caType,
      severityFilters,
    );
  }
  return undefined;
});

export const statusChartLabelsAtom = atom((get) => {
  const data: any = get(dashboardDataAtom);

  if (data?.vulns_by_status) {
    return Object.keys(
      data?.vulns_by_status[CAType.Server][Severity[Severity.Urgent]],
    );
  }
  return [];
});

/*************************/
// HELPER FUNCTIONS
/*************************/

function getVulnByStatusChartData(
  vulnByStatusData: any,
  caType: CAType,
  severityFilters: string[],
) {
  const server_values = vulnByStatusData[CAType.Server];
  const workstation_values = vulnByStatusData[CAType.Workstation];
  const dataArrays: number[][] = [];
  if (
    (caType === CAType.Server || caType === CAType.All) &&
    checkNonZeroObj(server_values)
  )
    dataArrays.push(server_values);
  if (
    (caType === CAType.Workstation || caType === CAType.All) &&
    checkNonZeroObj(workstation_values)
  )
    dataArrays.push(workstation_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  const resultArrays: number[][] = [];
  dataArrays.forEach((dataset) => {
    const result = { ACTIVE: 0, NEW: 0, REOPENED: 0, FIXED: 0, IGNORED: 0 };
    selectedSeverityFilters.forEach((severity) => {
      const data = dataset[severity];
      if (data) {
        Object.values(VulnerabilityStatus).forEach((status) => {
          result[status] += data[status];
        });
      }
    });
    resultArrays.push(Object.values(result));
  });
  return resultArrays;
}

function getVulnGroupsData(
  vulnGroupsData: any,
  caType: CAType,
  severityFilters: string[],
) {
  // sever or workstation filter
  const server_data = vulnGroupsData[CAType.Server];
  const workstation_data = vulnGroupsData[CAType.Workstation];
  const dataArrays: any[] = [];
  if (caType === CAType.Server || caType === CAType.All)
    dataArrays.push(server_data);
  if (caType === CAType.Workstation || caType === CAType.All)
    dataArrays.push(workstation_data);

  const result: GroupingDataItem[] = [];

  // severity filter
  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataArray) => {
    selectedSeverityFilters.forEach((severity) => {
      const dArray: GroupingDataItem[] = dataArray[severity];

      if (dArray) {
        dArray.forEach((item) => {
          // if already exists update counts
          const result_items = result.filter((x) => x.qid === item.qid);
          if (result_items?.length > 0) {
            result_items[0].count += item.count;
          }
          // otherwise insert a copy
          else {
            result.push({ ...item });
          }
        });
      }
    });
  });
  return result.length > 0 ? result : EMPTY_GROUPING_DATA;
}

function geObsoleteVulnGroupsData(vulnGroupsData: any, caType: CAType) {
  // sever or workstation filter
  const server_data = vulnGroupsData[CAType.Server];
  const workstation_data = vulnGroupsData[CAType.Workstation];
  const dataArrays: any[] = [];
  if (caType === CAType.Server || caType === CAType.All)
    dataArrays.push(server_data);
  if (caType === CAType.Workstation || caType === CAType.All)
    dataArrays.push(workstation_data);

  const result: GroupingDataItem[] = [];
  dataArrays.forEach((dataArray) => {
    const dArray: GroupingDataItem[] = dataArray;

    if (dArray) {
      dArray.forEach((item) => {
        // if already exists update counts
        const result_items = result.filter((x) => x.qid === item.qid);
        if (result_items?.length > 0) {
          result_items[0].count += item.count;
        }
        // otherwise insert a copy
        else {
          result.push({ ...item });
        }
      });
    }
  });
  return result.length > 0 ? result : EMPTY_GROUPING_DATA;
}

function getVulnBySeverityChartDataCount(
  sourceData: any,
  caType: CAType,
  severityFilters: string[],
) {
  const server_values = sourceData[CAType.Server];
  const workstation_values = sourceData[CAType.Workstation];
  const dataArrays: number[][] = [];
  const resultsArray: number[][] = [];
  if (caType === CAType.Server || caType === CAType.All)
    dataArrays.push(server_values);
  if (caType === CAType.Workstation || caType === CAType.All)
    dataArrays.push(workstation_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataset) => {
    const result: number[] = [];
    const severities = ['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal'];
    severities.forEach((severity) => {
      if (selectedSeverityFilters.includes(severity)) {
        result.push(dataset[severity]);
      } else {
        result.push(0);
      }
    });
    resultsArray.push(Object.values(result));
  });
  let count = 0;
  resultsArray.forEach((dataset) => {
    count += dataset.reduce((partialSum, x) => partialSum + x, 0);
  });
  return count;
}

function getVulnBySeverityChartData(sourceData: any, caType: CAType) {
  const server_values = Object.values<number>(sourceData[CAType.Server]);
  const workstation_values = Object.values<number>(
    sourceData[CAType.Workstation],
  );
  const resultsArray: number[][] = [];
  if (
    (caType === CAType.Server || caType === CAType.All) &&
    checkNonZeroArray(server_values)
  )
    resultsArray.push(server_values);
  if (
    (caType === CAType.Workstation || caType === CAType.All) &&
    checkNonZeroArray(workstation_values)
  )
    resultsArray.push(workstation_values);
  return resultsArray;
}

function getHistoryChartData(
  historicalData: any,
  status: VulnStatus,
  caType: CAType,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  const serversDataArray: number[][] = [];
  const workstationsDataArray: number[][] = [];
  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  selectedSeverityFilters.forEach((severityFilter) => {
    serversDataArray.push(
      historicalData[propertyName][severityFilter]?.Server[status],
    );
    workstationsDataArray.push(
      historicalData[propertyName][severityFilter]?.Workstations[status],
    );
  });

  const serversData: number[] = mergeArrays(serversDataArray);
  const workstationsData: number[] = mergeArrays(workstationsDataArray);
  const intervalFilterType =
    status === VulnStatus.All
      ? IntervalFilterType.SkipRecords
      : IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  if (caType === CAType.Server || caType === CAType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(serversData, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );
  if (caType === CAType.Workstation || caType === CAType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(workstationsData, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );

  return resultArray;
}

function getHistoryChartIntervalData(
  historicalData: any,
  status: VulnStatus,
  caType: CAType,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  const serversDataArray: any = [];
  const workstationsDataArray: any = [];
  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  const statusIntervalFilterKey = `${status.toLowerCase()}_${intervalFilter}`;

  selectedSeverityFilters.forEach((severityFilter) => {
    serversDataArray.push(
      historicalData[propertyName][severityFilter]?.Server[
        statusIntervalFilterKey
      ],
    );
    workstationsDataArray.push(
      historicalData[propertyName][severityFilter]?.Workstations[
        statusIntervalFilterKey
      ],
    );
  });

  const serversData: number[] = serversDataArray;
  const workstationsData: number[] = workstationsDataArray;
  const intervalFilterType = IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  if (caType === CAType.Server || caType === CAType.All)
    resultArray.push(
      applyIntervalFilter(serversData, intervalFilter, intervalFilterType),
    );
  if (caType === CAType.Workstation || caType === CAType.All)
    resultArray.push(
      applyIntervalFilter(workstationsData, intervalFilter, intervalFilterType),
    );

  return resultArray;
}

function getSelectedSeverityFilters(severityFilters: string[]) {
  let selectedSeverityFilters = severityFilters;
  if (severityFilters.includes(Severity[Severity.All])) {
    selectedSeverityFilters = [
      Severity[Severity.Urgent],
      Severity[Severity.Critical],
      Severity[Severity.Medium],
      Severity[Severity.Serious],
      Severity[Severity.Minimal],
    ];
  }
  return selectedSeverityFilters;
}
